import { Storage, ContentType, AdobePass } from 'enums';
import {
    getOmnitureAuthData,
    getOmnitureEntitlementsData,
    getOmniturePageData,
    getOmniturePageName,
    getVideoTypeFromContent,
    getVideoTypeFromStream,
    getOmnitureUserTrackingData
} from './format';
import { dotComNielsenData } from 'constants';
const { METADATA: { UPSTREAM_USERID } } = AdobePass;

// nielsen uses this as well to format pageName and videoType
export { 
    getOmniturePageName, 
    getVideoTypeFromContent, 
    getVideoTypeFromStream, 
    getOmnitureQuery,
    getOmnitureUserTrackingData
} from './format';

export const OMNITURE_CONSTANTS = {
    Channel: 'web',
    Site: 'watchespn',
    ReportSuite: 'wdgespcom',
    ReportSuiteQA: 'wdgespncomdev',
    ReportSuiteIntl: 'wdgesp360international',
    ReportSuiteApp: 'wdgesp360netunoven',
    TrackingServer: 'w88.espn.com',
    SecureTrackingServer: 'sw88.espn.com'
};

// will be injected by the `init` call
let baseOmnitureData;
let initialized = false;
let initializing = false;
let trackingQueue = [];
const { Analytics: { IS_AUTHENTICATING } } = Storage;

/**
  * This function calls the actual omniture page tracking
  */
function _trackPage(omnitureData = {}, bootstrap = {}) {
    if (!Object.keys(omnitureData).length) {
        return;
    }
    if (window.espn && window.espn.track) {
        const espnTrack = window.espn.track;
        if (!initialized && espnTrack.init && espnTrack.trackPage) {
            initialized = true;
            initializing = true;

            const nielsenData = espnTrack.data && espnTrack.data.nielsen;
            espnTrack.init({ omniture: omnitureData, nielsen: nielsenData || dotComNielsenData });
            // espn.track.init() invokes async data fetch which we won't know when to complete
            // FIXME: we need to know when exactly all the remote data is ready w/o arbitrary timeout.
            setTimeout(() => {
                initializing = false;
                try {
                    trackingQueue.forEach(queuedData => {
                        espnTrack.trackPage(queuedData);
                    });
                    // TODO: DTC - remove this once https://code.espn.com/webdev/score/pull/9035 lands
                    const { bam: { env, playerdomain: playerdomainArg } = {} } = bootstrap;
                    if (window._sf_async_config && playerdomainArg) {
                        let playerdomain = playerdomainArg;
                        if (
                            (env === 'beta' || /bamenv=beta($|[^_])/.test(window.location.search)) &&
                            playerdomain.indexOf('espnqa') > -1
                        ) {
                            playerdomain = playerdomain.replace('espnqa', 'espn');
                        }
                        window._sf_async_config.playerdomain = playerdomain;
                    }
                } catch (err) {
                    console.error(err);
                }
                trackingQueue = [];
            }, 2000);
        } else if (initializing) {
            trackingQueue.push(omnitureData);
        } else if (espnTrack.trackPage) {
            espnTrack.trackPage(omnitureData);
        }
    } else {
        trackByIframe('page', omnitureData);
    }
}

/**
  * This function calls the actual omniture link tracking
  */
function _trackLink(data = {}) {
    if (window.espn && window.espn.track && window.espn.track.clear && window.espn.track.trackLink) {
        window.espn.track.clear();
        const linkData = { ...data, link: 'true' };
        window.espn.track.userClicked = true;
        window.espn.track.trackLink(linkData);
    } else {
        trackByIframe('link', data);
    }
}

export function trackByIframe(type, data) {
    const baseUrl = window.location.protocol === 'https:' ?
        'https://secure.espn.com' : 'http://espn.com';

    data.type = type;
    data.content = data.contentType;
    data.source = data.site;
    data.pagename = data.pageName;

    const hash = Object.keys(data).reduce((acc, key) => (
        `${acc}${acc ? '&' : ''}${key}=${encodeURIComponent(data[key])}`
    ), '');
    const trackingUrl = `${baseUrl}/core/tracking/iframe#${hash}`;
    const id = 'espn-tracking-iframe';
    let iframe = document.getElementById(id);
    if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
    }
    iframe = document.createElement('iframe');
    iframe.id = id;
    iframe.src = trackingUrl;
    iframe.width = '0';
    iframe.height = '0';
    iframe.frameBorder = '0';
    document.body.appendChild(iframe);
}

export function init(constants) {
    baseOmnitureData = {
        site: constants.Site,
        channel: constants.Channel,
        account: constants.ReportSuite,
        premium: 'premium-no',
        eVar1: 'DoNotSet',
        eVar12: constants.Edition,
        prop1: 'watchespn',
        prop16: constants.Edition
    };
}

export function clearTrackingData() {
    localStorage.removeItem(IS_AUTHENTICATING);
}

export function trackMvpdPicker({ isDomestic }) {
    const mvpdPickerData = {
        events: 'event3,event49,event67',
        pageName: 'mvpd picker',
        eVar13: 'mvpd picker',
        prop4: 'authentication',
        prop5: 'authentication',
        eVar11: `authentication:${baseOmnitureData.site}:authentication`,
    };
    const omnitureData = {
        ...baseOmnitureData,
        ...mvpdPickerData,
        ...getOmnitureAuthData([], {}, isDomestic)
    };
    _trackPage(omnitureData);
}

export function trackProviderSelect({ userAuth, provider, entitlementsTracking, isDomestic }) {
    const providerName = provider.package ? provider.abbreviation : provider.name;
    const providerChosen = `provider chosen:${providerName}`;
    const providerSelectData = {
        events: 'event72',
        pev2: providerName,
        prop9: providerChosen,
        eVar74: providerChosen
    };
    const omnitureData = {
        ...baseOmnitureData,
        ...getOmnitureAuthData(userAuth, provider, isDomestic),
        ...getOmnitureEntitlementsData(entitlementsTracking),
        ...providerSelectData
    };
    _trackLink(omnitureData);
    localStorage.setItem(IS_AUTHENTICATING, 'true');
}

export function trackAuthenticationComplete({ userAuth, provider, entitlementsTracking, isDomestic }) {
    const isAuthenticating = localStorage.getItem(IS_AUTHENTICATING);
    if (isAuthenticating === 'true' || isAuthenticating === true) {
        const authenticationComplete = 'authentication complete';
        const authenticationCompleteData = {
            events: 'event68',
            pev2: authenticationComplete,
            prop9: authenticationComplete,
            eVar74: authenticationComplete
        };
        const omnitureData = {
            ...baseOmnitureData,
            ...getOmnitureAuthData(userAuth, provider, isDomestic),
            ...getOmnitureEntitlementsData(entitlementsTracking),
            ...authenticationCompleteData
        };
        _trackLink(omnitureData);
        clearTrackingData();
    }
}

export function trackAuthorizationStart({ userAuth, provider, entitlementsTracking, isDomestic }) {
    const authorizationStart = 'authorization start';
    const authorizationStartData = {
        events: 'event69',
        pev2: authorizationStart,
        prop9: authorizationStart,
        eVar74: authorizationStart
    };
    const omnitureData = {
        ...baseOmnitureData,
        ...getOmnitureAuthData(userAuth, provider, isDomestic),
        ...getOmnitureEntitlementsData(entitlementsTracking),
        ...authorizationStartData
    };
    setTimeout(() => {
        _trackLink(omnitureData);
    }, 1000);
}

export function trackAuthorizationComplete({ userAuth, provider, entitlementsTracking, isDomestic }) {
    const authorizationComplete = 'authorization complete';
    const authorizationCompleteData = {
        events: 'event70',
        pev2: authorizationComplete,
        prop9: authorizationComplete,
        eVar74: authorizationComplete
    };
    const omnitureData = {
        ...baseOmnitureData,
        ...getOmnitureAuthData(userAuth, provider, isDomestic),
        ...getOmnitureEntitlementsData(entitlementsTracking),
        ...authorizationCompleteData
    };
    setTimeout(() => {
        _trackLink(omnitureData);
    }, 1000);

}
// https://espnjira.disney.com/browse/WEWEB-1174
export function trackSearch({
    fromHero = false,
    searchTerm = '',
    isSuggestion = false,
    isViewAllResults = false,
    type,
    status,
    entitlementsTracking
}) {
    const context = (fromHero === true ? 'search-hero' : 'search-page');
    const searchType = isSuggestion ? 'pre' : 'man';
    let submitMethod = 'submit';
    if (isSuggestion) {
        submitMethod = 'suggested';
        if (isViewAllResults) {
            submitMethod = 'view+all+results'
        } else if (type === ContentType.VOD) {
            submitMethod = 'suggested-vod-clip';
        } else if (type && status) {
            submitMethod = `suggested-${status}-${type}`;
        }
    }
    const searchTrackData = {
        events: 'event2',
        eVar4: `${searchType}:${searchTerm}`,
        prop7: `${searchType}:${searchTerm}`,
        eVar5: `${submitMethod}`,
        eVar74: context,
        prop9: context
    };
    const omnitureData = {
        ...baseOmnitureData,
        ...searchTrackData,
        ...getOmnitureEntitlementsData(entitlementsTracking)
    };
    _trackLink(omnitureData);
}

export function trackUntimelyLogout(actual = 'unknown', expected = 'unknown', provider, entitlementsTracking) {
    const untimelyLogout = 'Untimely Logout';

    let providerName = provider.name;
    let providerAbbreviation = provider.abbreviation;
    if (provider.package) {
        providerName = provider.abbreviation;
        providerAbbreviation = provider.package.name;
    }

    const authnData = {
        eVar17: providerName,
        prop42: providerName,
        eVar32: providerAbbreviation,
        prop43: providerAbbreviation
    };

    const untimelyLogoutData = {
        events: 'event123',
        eVar37: navigator.userAgent,
        eVar74: untimelyLogout,
        eVar81: `actual:${actual}|expected:${expected}`,
        prop37: navigator.userAgent,
        prop9: untimelyLogout
    };
    const omnitureData = {
        ...baseOmnitureData,
        ...authnData,
        ...untimelyLogoutData,
        ...getOmnitureEntitlementsData(entitlementsTracking)
    };
    _trackLink(omnitureData);
}

export function trackLeavePageAlertShown() {
    const leavePageAlertShown = 'Leave Page Alert Shown';
    const leavePageData = {
        events: 'event120',
        eVar74: leavePageAlertShown,
        eVar72: leavePageAlertShown,
        prop9: leavePageAlertShown
    };
    const omnitureData= {
        ...baseOmnitureData,
        ...leavePageData
    };
    _trackLink(omnitureData);
}

export function trackSocialShareClick(content, page, provider, socialPlatform, userAuth, entitlementsTracking, isDomestic) {
    const authData = getOmnitureAuthData(userAuth, provider, isDomestic);

    const socialShareValue = `social share:${getOmniturePageName(page)}:${socialPlatform}:${content.name}`;

    const omnitureData = {
        events: 'event62',
        pev2: socialShareValue,
        prop9: socialShareValue,
        eVar72: `social share:${socialPlatform}:${content.type}`,
        eVar74: socialShareValue,
        ...authData,
        ...getOmnitureEntitlementsData(entitlementsTracking)
    };

    _trackLink(omnitureData);
}

export function trackReturnAfterLeavePageAlert() {
    const returnAfterLeavePageAlert = 'Return After Leave Page Alert';
    const leavePageData = {
        events: 'event121',
        eVar74: returnAfterLeavePageAlert,
        eVar72: returnAfterLeavePageAlert,
        prop9: returnAfterLeavePageAlert
    };
    const omnitureData= {
        ...baseOmnitureData,
        ...leavePageData
    };
    _trackLink(omnitureData);
}

export function trackPage({ app, page, provider, userAuth, entitlementsTracking, promoTracking, adobePassMetadata = {}, isDomestic }) {
    const lang = (app.lang || '').toLowerCase();
    let { [UPSTREAM_USERID]: usid } = adobePassMetadata;
    const pageTrackData = {
        events: 'event3',
        prop17: lang,
        eVar9: lang,
        eVar85: usid
    };

    if (promoTracking) {
        pageTrackData.events = 'event3,prodView,event131';
        pageTrackData.products = promoTracking;
    }

    const omnitureData = {
        ...baseOmnitureData,
        ...getOmniturePageData(page),
        ...getOmnitureAuthData(userAuth, provider, isDomestic),
        ...getOmnitureEntitlementsData(entitlementsTracking),
        ...pageTrackData
    };
    _trackPage(omnitureData, app.bootstrap);
}

export function trackActivatePage(lang, device = 'unknown') {
    const trackingLang = lang.toLowerCase();
    const pageTrackData = {
        events: 'event3,event90',
        prop5: device,
        prop17: trackingLang,
        eVar9: trackingLang,
        eVar11: `activate:watchespn:${device}`,
        pageName: `${device}:activate`
    };

    const omnitureData = {
        ...baseOmnitureData,
        ...pageTrackData
    };
    _trackPage(omnitureData);
}

export function trackActivateCodeInvalid(device = 'unknown', provider = {}) {
    const { abbreviation: mvpdId, name: mvpdName } = provider;
    const invalidCode = `${device}:activate:invalidcode`;
    const invalidCodeData = {
        events: 'event90',
        prop9: invalidCode,
        eVar74: invalidCode,
        eVar32: mvpdId,
        prop43: mvpdId,
        eVar17: mvpdName,
        prop42: mvpdName,
        eVar79: `${device}:activationfail`

    };
    const omnitureData = {
        ...baseOmnitureData,
        ...invalidCodeData
    };
    _trackLink(omnitureData);
}

export function trackActivateSuccess(device = 'unknown', provider = {}) {
    const { abbreviation: mvpdId, name: mvpdName } = provider;
    const success = `${device}:activate:success`;
    const successData = {
        pageName: `${device}:activate`,
        events: 'event90',
        eVar11: `activate:watchespn:${device}`,
        prop9: success,
        eVar74: success,
        eVar32: mvpdId,
        prop43: mvpdId,
        eVar17: mvpdName,
        prop42: mvpdName,
        eVar79: `${device}:activationsuccess`
    };
    const omnitureData = {
        ...baseOmnitureData,
        ...successData
    };
    _trackPage(omnitureData);
}

// prop9 translates to c9, and eVar74 translates to v74
export function trackEspnPlayerOnAdSkipped() {
    const omnitureData = {
        prop1: 'watchespn',
        prop9: 'preroll+ad+skip',
        eVar74: 'preroll+ad+skip',
        pev2: 'Preroll Ad Skip',
    };
    
    _trackLink(omnitureData);
}


// WEBPACK FOOTER //
// ./app/lib/omniture/index.js